import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    UserPlus,
    Search,
    Eye,
    SquarePen,
    Trash2,
    Users,
    UserCheck,
    UserMinus,
    Shield,
    ChevronDown,
    RefreshCw,
    Loader2
} from 'lucide-react';
import AddUserModal from '../components/AddUserModal';
import DeleteConfirmationModal from '../../../shared/components/modals/DeleteConfirmationModal';
import { initialDummyUsers } from '../data/mockUsers';
import { fetchAllUsers, fetchEveryUser, readUsersPage, registerUser, fetchRoles, deleteUser, normalizeRoleItem, normalizeUserData } from '../services/adminService';
import { USERS_PAGE_SIZE } from '../constants/userListConfig';
import Pagination from '../../../shared/components/ui/Pagination';
import { useScrollToTop } from '../../../utils/scrollUtils';
import './UsersRoles.css';

const UsersRoles = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState(''); // role code sent to the server; '' = all roles
    const [page, setPage] = useState(1);
    const [pageInfo, setPageInfo] = useState({ total: initialDummyUsers.length, totalPages: 1 });
    const latestRequest = useRef(0); // lets a slow, older answer be ignored
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useScrollToTop([roleFilter, page]);

    // Initialized with mock data as graceful fallback
    const [users, setUsers] = useState(initialDummyUsers);
    // Every team lead, for the "Add User" dialog (the table only holds one page)
    const [teamLeads, setTeamLeads] = useState([]);
    const [roles, setRoles] = useState([
        { value: 'employee', label: 'Team Member (Employee)', code: 'employee' },
        { value: 'team_lead', label: 'Team Lead', code: 'team_lead' },
        { value: 'admin', label: 'Admin', code: 'admin' }
    ]);

    // Fetch roles from GET /rfp/auth/roles
    const loadRoles = useCallback(async () => {
        try {
            const res = await fetchRoles();
            const raw = res?.data;
            const list = Array.isArray(raw)
                ? raw
                : (Array.isArray(raw?.roles) ? raw.roles : (Array.isArray(raw?.data) ? raw.data : null));
            if (list && list.length > 0) {
                const normalized = list.map(normalizeRoleItem).filter(Boolean);
                if (normalized.length > 0) {
                    setRoles(normalized);
                }
            }
        } catch (err) {
            console.error('Failed to fetch roles from /rfp/auth/roles:', err);
        }
    }, []);

    // Fetch one page of users from GET /rfp/auth/users?page=&page_size=&role=
    const loadUsers = useCallback(async (isSilent = false) => {
        const requestId = ++latestRequest.current;
        if (isSilent) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        try {
            const response = await fetchAllUsers({ page, page_size: USERS_PAGE_SIZE, role: roleFilter });
            if (requestId !== latestRequest.current) return;

            const result = readUsersPage(response);
            // The page is gone (e.g. the last user of the last page was deleted): go back one.
            if (result.items.length === 0 && page > 1 && result.totalPages < page) {
                setPage(result.totalPages);
                return;
            }
            setUsers(result.items.map(normalizeUserData));
            setPageInfo({ total: result.total, totalPages: result.totalPages });
        } catch (error) {
            console.error('Error fetching users from /rfp/auth/users:', error);
            // Graceful fallback: maintain existing list (or initialDummyUsers)
        } finally {
            if (requestId === latestRequest.current) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, [page, roleFilter]);

    const loadTeamLeads = useCallback(async () => {
        try {
            const rawLeads = await fetchEveryUser({ role: 'team_lead' });
            setTeamLeads(rawLeads.map(normalizeUserData));
        } catch (error) {
            console.error('Error fetching team leads from /rfp/auth/users:', error);
        }
    }, []);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    useEffect(() => {
        loadRoles();
        loadTeamLeads();
    }, [loadRoles, loadTeamLeads]);

    const handleRoleFilterChange = (event) => {
        setRoleFilter(event.target.value);
        setPage(1);
    };

    // Total comes from the server; the other counts only cover the users on this page.
    const stats = [
        {
            label: 'TOTAL USERS',
            value: pageInfo.total.toString(),
            icon: <Users size={18} />,
            color: '#4167BF',
            bg: 'rgba(65, 103, 191, 0.1)'
        },
        {
            label: 'ACTIVE',
            value: users.filter(u => u.status === 'active').length.toString(),
            icon: <UserCheck size={18} />,
            color: '#10B981',
            bg: 'rgba(16, 185, 129, 0.1)'
        },
        {
            label: 'INACTIVE',
            value: users.filter(u => u.status !== 'active').length.toString(),
            icon: <UserMinus size={18} />,
            color: '#64748B',
            bg: 'rgba(100, 116, 139, 0.1)'
        },
        {
            label: 'ROLES',
            value: users.length > 0 ? new Set(users.map(u => u.role)).size.toString() : '0',
            icon: <Shield size={18} />,
            color: '#FF5526',
            bg: 'rgba(255, 85, 38, 0.1)'
        },
    ];

    const getRoleBadgeClass = (role) => {
        const r = (role || '').toLowerCase();
        if (r.includes('admin')) return 'admin';
        if (r.includes('lead') || r.includes('manager')) return 'team-lead';
        return 'team-member';
    };

    const handleAddUser = async (newUser) => {
        const payload = {
            email: newUser.email.trim(),
            first_name: newUser.first_name.trim(),
            last_name: newUser.last_name.trim(),
            role: newUser.role || 'employee',
            designation: newUser.designation || (newUser.role === 'team_lead' ? 'Team Lead' : newUser.role === 'admin' ? 'Admin' : 'Team Member'),
            department: newUser.department || 'AI',
            location: newUser.location || ''
        };

        // The phone number is optional: it is only sent when filled in. No password is sent; the user sets their own at first login.

        const phone = (newUser.phoneNumber || newUser.phone_number || '').trim();

        if (phone) payload.phone_number = phone;

        await registerUser(payload);
        // The list is sorted by name on the server, so reload the page instead of inserting the user.
        loadUsers(true);
        loadTeamLeads();
    };

    const handleConfirmDeleteUser = async (targetUser) => {
        const userObj = targetUser || userToDelete;
        const id = userObj?._id || userObj?.id;
        if (!id) return false;

        setDeletingId(String(id));
        try {
            await deleteUser(id);
            setUsers(prev => prev.filter(u => String(u.id) !== String(id) && String(u._id) !== String(id)));
            setShowDeleteModal(false);
            setUserToDelete(null);
            // Sync with backend (also steps back a page if this one is now empty)
            loadUsers(true);
            loadTeamLeads();
            return true;
        } catch (err) {
            console.error('Failed to delete user:', err);
            return false;
        } finally {
            setDeletingId(null);
        }
    };

    // The role filter is applied by the server; the search box filters the users on this page.
    const filteredUsers = users.filter(user => {
        // Search query
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            return (
                (user.name && user.name.toLowerCase().includes(q)) ||
                (user.email && user.email.toLowerCase().includes(q)) ||
                (user.role && user.role.toLowerCase().includes(q)) ||
                (user.department && user.department.toLowerCase().includes(q)) ||
                (user.designation && user.designation.toLowerCase().includes(q))
            );
        }
        return true;
    });

    return (
        <div className="users-roles-container">
            <header className="page-header">
                <div className="header-content">
                    <h3>Users & Roles</h3>
                    <p>Manage users, assign roles, and control access permissions</p>
                </div>
                <div className="header-actions">
                    <button
                        className="manage-roles-btn"
                        onClick={() => loadUsers(true)}
                        disabled={loading || refreshing}
                        title="Refresh users from server"
                    >
                        <RefreshCw size={15} className={refreshing ? 'spinning-icon' : ''} />
                        Refresh
                    </button>
                    <button className="add-user-btn" onClick={() => setIsAddUserModalOpen(true)}>
                        <UserPlus size={16} />
                        Add User
                    </button>
                </div>
            </header>

            <section className="kpi-grid">
                {stats.map((stat, i) => (
                    <div key={i} className="kpi-card-modern">
                        <div className="kpi-icon-box" style={{ backgroundColor: stat.bg, color: stat.color }}>
                            {stat.icon}
                        </div>
                        <div className="kpi-info">
                            <span className="kpi-label">{stat.label}</span>
                            <span className="kpi-value">{stat.value}</span>
                        </div>
                    </div>
                ))}
            </section>

            <div className="users-table-wrapper">
                {/* Search and role filter */}
                <div className="table-controls-modern">
                    <div className="search-bar-modern">
                        <Search size={16} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search users by name or email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
    
                    <div className="role-dropdown-wrapper">
                        <select
                            className="role-dropdown-select"
                            value={roleFilter}
                            onChange={handleRoleFilterChange}
                        >
                            <option value="">All Roles</option>
                            {roles.map((r) => {
                                const displayLabel = r.label.replace(' (Employee)', '');
                                return (
                                    <option key={r.value || r.code} value={r.value || r.code}>
                                        {displayLabel}
                                    </option>
                                );
                            })}
                        </select>
                        <ChevronDown size={14} className="dropdown-arrow" />
                    </div>
                </div>
                <table className="users-table">
                    <thead>
                        <tr>
                            <th className="th-user">User</th>
                            <th className="th-role">Role</th>
                            <th className="th-lead">Assigned Lead</th>
                            <th className="th-actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={4} className="empty-table-cell">
                                    <div className="empty-table-state">
                                        <Loader2 size={30} className="spinning-icon" style={{ color: '#FF5526' }} />
                                        <p className="empty-title" style={{ marginTop: '0.75rem' }}>Loading users...</p>
                                        <p className="empty-subtitle">Fetching latest users from API</p>
                                    </div>
                                </td>
                            </tr>
                        ) : filteredUsers.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="empty-table-cell">
                                    <div className="empty-table-state">
                                        <Users size={32} className="empty-icon" />
                                        <p className="empty-title">No users found</p>
                                        <p className="empty-subtitle">
                                            {searchQuery || roleFilter
                                                ? 'No user records matching your current filter.'
                                                : 'No user records available.'}
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredUsers.map(user => {
                                const roleClass = getRoleBadgeClass(user.role);
                                return (
                                    <tr
                                        key={user.id}
                                        className="user-table-row clickable-row"
                                        onClick={() => navigate(`/users-roles/${user.id}`, { state: { user } })}
                                        title="Click to view user profile"
                                    >
                                        <td className="user-cell-wrapper">
                                             <div className="user-cell">
                                                <div className="user-avatar" style={{ backgroundColor: user.avatarColor }}>
                                                    {user.initials}
                                                </div>
                                                <span className="user-cell-text">
                                                    <span className="user-name">{user.name}</span>
                                                    {user.email && <span className="user-email">{user.email}</span>}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="role-cell">
                                            <span className={`role-badge ${roleClass}`}>
                                                {user.role}
                                            </span>
                                        </td>
                                        <td className="lead-cell">{user.manager_name || '-'}</td>
                                        <td className="actions-cell">
                                            <div className="actions-wrapper">
                                                <button
                                                    className="action-btn"
                                                    title="View User"
                                                    aria-label={`View ${user.name}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/users-roles/${user.id}`, { state: { user } });
                                                    }}
                                                >
                                                    <Eye size={15} />
                                                </button>
                                                <button
                                                    className="action-btn"
                                                    title="Edit User"
                                                    aria-label={`Edit ${user.name}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/users-roles/${user.id}`, { state: { user } });
                                                    }}
                                                >
                                                    <SquarePen size={15} />
                                                </button>
                                                <button
                                                    className="action-btn delete"
                                                    title="Deactivate / Remove User"
                                                    aria-label={`Remove ${user.name}`}
                                                    disabled={deletingId === String(user.id || user._id)}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setUserToDelete({ ...user, title: user.name || user.email || 'this user' });
                                                        setShowDeleteModal(true);
                                                    }}
                                                >
                                                    {deletingId === String(user.id || user._id) ? (
                                                        <Loader2 size={15} className="spinning-icon" />
                                                    ) : (
                                                        <Trash2 size={15} />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <Pagination
                page={page}
                totalPages={pageInfo.totalPages}
                totalItems={pageInfo.total}
                pageSize={USERS_PAGE_SIZE}
                onPageChange={setPage}
                disabled={loading || refreshing}
                itemLabel="users"
            />

            <AddUserModal
                isOpen={isAddUserModalOpen}
                onClose={() => setIsAddUserModalOpen(false)}
                onAddUser={handleAddUser}
                teamLeads={teamLeads}
                roles={roles}
            />

            {showDeleteModal && userToDelete && (
                <DeleteConfirmationModal
                    project={userToDelete}
                    customTitle="Delete User?"
                    customMessage={<>Are you sure you want to permanently remove <strong>"{userToDelete.name || 'this user'}"</strong>?</>}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setUserToDelete(null);
                    }}
                    onDeleteConfirm={handleConfirmDeleteUser}
                />
            )}
        </div>
    );
};

export default UsersRoles;

